/**
 * Tatbikat kayıtları ve raporları (Faz 5; ADR 0128 Faz 5 Ek; ortağın belgesi §8 "Drill Records &
 * Reports"): Part A–E PDF'i, denetim izi, Records arşivi (süzgeç, arama, toplu PDF ve ZIP, Legacy
 * satırı), filo analitiği ve yetkinlik matrisinin dışa aktarımı. Belgenin bölüm adları İngilizcede
 * aynen (Part A – General Information … Part E – Approval).
 */

export const drillReportEn = {
  title: 'Drill Report',
  bulkTitle: 'Drill Records',
  actions: {
    pdf: 'Download PDF',
    print: 'Print',
    exportPdf: 'Export PDF',
    exportZip: 'Export ZIP',
    exportExcel: 'Export Excel',
    exportPdfHint: 'One PDF with the selected records (Part A–E each).',
    exportZipHint: 'One PDF per record and a manifest with SHA-256 hashes.',
    selectAll: 'Select all shown records',
    select: 'Select {{no}}',
    clear: 'Clear selection',
  },
  marks: {
    notApproved: 'NOT APPROVED – {{state}}',
    reopened: 'REOPENED – UNDER CORRECTION',
    stopped: 'STOPPED – REAL EMERGENCY',
    cancelled: 'CANCELLED',
  },
  parts: {
    a: 'Part A – General Information',
    b: 'Part B – Preparation & Execution',
    c: 'Part C – Evaluation',
    d: 'Part D – Corrective Actions',
    e: 'Part E – Approval',
    version: 'Template and Version',
    attachments: 'Attachments',
    history: 'Change History',
  },
  sub: {
    preparation: 'Preparation',
    checklist: 'Preparation checklist',
    participants: 'Participants',
    steps: 'Execution steps',
    criteria: 'Performance criteria',
    crew: 'Crew performance',
    deficiencies: 'Deficiencies',
    stop: 'Stopped: real emergency',
  },
  fields: {
    vessel: 'Vessel',
    recordNo: 'Record No.',
    date: 'Date',
    time: 'Time (ship time)',
    duration: 'Duration',
    location: 'Location',
    drill: 'Drill',
    category: 'Drill type',
    responsible: 'Responsible Officer',
    scenario: 'Scenario',
    briefing: 'Safety briefing given',
    briefingNotes: 'Briefing notes',
    equipmentReady: 'Equipment ready',
    equipmentNotes: 'Equipment notes',
    weather: 'Weather',
    constraints: 'Operational constraints',
    ra: 'Risk assessment verification',
    riskNotes: 'Risk notes',
    attendance: 'Attendance',
    steps: 'Steps',
    score: 'Score',
    debriefing: 'Debriefing',
    template: 'Template',
    templateVersion: 'Template version',
    library: 'Master Library',
    vesselScenario: 'Vessel scenario',
    contentHash: 'Content hash',
    status: 'Status',
    stopReason: 'Reason',
    stoppedAt: 'Stopped at',
    incident: 'Incident report',
    cancelReason: 'Reason for cancellation',
  },
  columns: {
    item: 'Item',
    done: 'Done',
    name: 'Name',
    rank: 'Rank',
    role: 'Role',
    attendance: 'Attendance',
    absence: 'Reason for absence',
    no: 'No.',
    step: 'Step',
    result: 'Result',
    time: 'Time',
    responsible: 'Responsible',
    remarks: 'Remarks',
    evidence: 'Evidence',
    criterion: 'Criterion',
    value: 'Result',
    note: 'Note',
    rating: 'Rating',
    finding: 'Finding No.',
    type: 'Type',
    description: 'Description',
    due: 'Target Date',
    linked: 'Linked record',
    followUp: 'Follow-up',
    status: 'Status',
    file: 'File',
    size: 'Size',
    sha: 'SHA-256',
    open: 'Open',
    at: 'Time (UTC)',
    event: 'Event',
    by: 'By',
    detail: 'Detail',
  },
  approval: {
    submitted: 'Submitted for Master review',
    decision: 'Master review: {{decision}}',
    reopened: 'Reopened',
    responsibleRole: 'Responsible Officer',
    master: 'Master',
    deputy: 'On behalf of Master',
    hash: 'Content {{hash}}',
  },
  none: {
    checklist: 'No checklist items.',
    participants: 'No participants.',
    steps: 'No steps.',
    findings: 'No findings raised.',
    attachments: 'No evidence attached.',
    crew: 'No crew performance recorded.',
    deficiencies: 'No deficiencies recorded.',
    history: 'No history.',
  },
  values: {
    yes: 'Yes',
    no: 'No',
    present: 'Present',
    absent: 'Absent',
    presentCount: '{{present}} of {{total}} present',
    stepsCount: '{{completed}} completed, {{notCompleted}} not completed, {{na}} N/A of {{total}}',
    score:
      '{{percent}}% satisfactory ({{satisfactory}} of {{rated}}); company method, not a regulatory requirement',
    noScore: 'No rated criterion',
    version: 'v{{version}}',
    libraryNow: 'Library now v{{version}}; this record keeps v{{pinned}}',
    libraryCurrent: 'Current (v{{version}})',
    scenarioYes: 'Approved vessel scenario',
    scenarioNo: 'Master Library template',
    minutes: '{{count}} min',
    evidence: '{{count}} file(s)',
    attachmentCaption: 'Step {{no}} · {{name}}',
    voided: 'Voided',
    notEmbedded: 'Not embedded; open the file',
    openLink: 'Open file (office sign-in)',
    openInApp: 'Open in Felixmate: record, step {{no}}',
    redrill: 'Re-drill requested',
    workOrders: 'Work orders: {{list}}',
    requisitions: 'Requisitions: {{list}}',
  },
  deficiency: {
    equipment: 'Equipment not ready: {{note}}',
    step: 'Step {{no}} not completed: {{text}}',
    criterion: '{{criterion}}: Needs Improvement',
  },
  versionNote:
    'The record keeps the template version it was started with; later Master Library changes do not alter it (ISM Drill Management §13.1).',
  attachmentsNote:
    'Photos are shown above; other files open from the link (office web app) or from the record in Felixmate. Evidence files are kept with the record on board and in the office; the SHA-256 values let an auditor verify them.',
  audit: {
    title: 'Audit Trail',
    hint: 'Creation, edits, submission, Master decision and reopening of this record, from the immutable event log and the change log.',
    empty: 'No history yet.',
    onBehalf: 'on behalf of Master',
    fields_one: '{{count}} field changed',
    fields_other: '{{count}} fields changed',
    kinds: {
      created: 'Created',
      edited: 'Edited',
      submitted: 'Submitted for Master review',
      approved: 'Approved by Master',
      returned: 'Returned by Master',
      reopened: 'Reopened for correction',
      stopped: 'Stopped: real emergency',
      cancelled: 'Cancelled',
      incident_linked: 'Incident report linked',
      finding_raised: 'Finding raised',
      finding_linked: 'Finding linked to a record',
      finding_cancelled: 'Finding cancelled',
    },
    sections: {
      general: 'General Information',
      preparation: 'Preparation',
      participants: 'Participants',
      execution: 'Execution',
      evaluation: 'Evaluation',
      findings: 'Findings',
    },
  },
  archive: {
    search: 'Search',
    searchPlaceholder: 'Record No., drill or officer',
    from: 'From',
    to: 'To',
    type: 'Drill type',
    allTypes: 'All types',
    selected_one: '{{count}} record selected',
    selected_other: '{{count}} records selected',
    limit: 'At most {{max}} records per export; narrow the filter or the selection.',
    preparing: 'Preparing {{done}} of {{total}}…',
    exported_one: '{{count}} record exported.',
    exported_other: '{{count}} records exported.',
    failed: 'The export could not be produced.',
    legacy: 'Legacy (ISM form)',
    legacyHint:
      'Drill report recorded in ISM Forms before the Drill module. It is not copied; the row opens the original form record.',
    legacyStatus: {
      pending: 'Awaiting approval',
      returned: 'Returned',
      approved: 'Approved',
      void: 'Void',
      inconsistent: 'Check record',
    },
  },
  analytics: {
    description:
      'Fleet comparison of drill compliance: completion rate, delays, muster and response times, open findings and drill types.',
    error: 'Drill analytics could not be loaded.',
    from: 'From',
    to: 'To',
    vessel: 'Vessel',
    allVessels: 'All vessels',
    type: 'Drill type',
    allTypes: 'All types',
    period: '{{from}} – {{to}}',
    kpi: {
      completionRate: 'Completion Rate',
      completed: 'Completed',
      late: 'Late or Missed',
      overdue: 'Overdue Now',
      muster: 'Avg. Muster Time',
      response: 'Avg. Response Time',
      openFindings: 'Open Findings',
    },
    columns: {
      vessel: 'Vessel',
      planned: 'Due in period',
      completed: 'Completed',
      completionRate: 'Completion rate',
      late: 'Late / missed',
      overdue: 'Overdue now',
      muster: 'Avg. muster',
      response: 'Avg. response',
      openFindings: 'Open findings',
      month: 'Month',
      records: 'Records',
      type: 'Drill type',
      count: 'Records',
      share: 'Share',
    },
    comparison: 'Vessel comparison',
    trend: 'Muster and response time trend',
    trendHint: 'Monthly average of Master-approved records (m:ss).',
    metric: 'Measure',
    metrics: { muster: 'Muster time', response: 'Response time' },
    types: 'Drill type distribution',
    typesHint: 'Master-approved records in the period.',
    fleet: 'Fleet',
    noData: 'No approved drill record in the period.',
    trendPoint: '{{vessel}}, {{month}}: {{value}}',
    sheets: { vessels: 'Vessels', trend: 'Trend', types: 'Drill types' },
    methodNote:
      'Completion rate: cycles due in the period completed on time. Overdue and open findings are today’s state. Times and types count Master-approved records only.',
    officeOnly: 'Fleet analytics is used in the office.',
  },
  competency: {
    pdf: 'PDF',
    excel: 'Excel',
    title: 'Crew Competency Matrix',
    sheet: 'Competency',
    person: 'Crew member',
    rank: 'Rank',
    last: 'Last drill',
    missing: 'Missing mandatory drill',
    trainingNeeds: 'Open training needs',
    cell: '{{date}} · {{role}} · {{rating}}',
    never: 'Not attended',
    mandatory: 'Mandatory monthly (SOLAS III/19.3.2)',
    legend:
      'Cell: last Master-approved participation (date, role, rating). “!” marks a missing mandatory fire or abandon ship drill (SOLAS III/19.3.2). Rating is the company method.',
  },
};

export const drillReportTr: typeof drillReportEn = {
  title: 'Tatbikat Raporu',
  bulkTitle: 'Tatbikat Kayıtları',
  actions: {
    pdf: 'PDF indir',
    print: 'Yazdır',
    exportPdf: 'PDF olarak dışa aktar',
    exportZip: 'ZIP olarak dışa aktar',
    exportExcel: 'Excel olarak dışa aktar',
    exportPdfHint: 'Seçili kayıtlar tek PDF’te (her biri Part A–E).',
    exportZipHint: 'Kayıt başına bir PDF ve SHA-256 değerli bir manifest.',
    selectAll: 'Görünen bütün kayıtları seç',
    select: '{{no}} seç',
    clear: 'Seçimi temizle',
  },
  marks: {
    notApproved: 'ONAYLANMADI – {{state}}',
    reopened: 'YENİDEN AÇILDI – DÜZELTİLİYOR',
    stopped: 'DURDURULDU – GERÇEK ACİL DURUM',
    cancelled: 'İPTAL EDİLDİ',
  },
  parts: {
    a: 'Part A – Genel Bilgiler',
    b: 'Part B – Hazırlık ve İcra',
    c: 'Part C – Değerlendirme',
    d: 'Part D – Düzeltici Faaliyetler',
    e: 'Part E – Onay',
    version: 'Şablon ve Sürüm',
    attachments: 'Ekler',
    history: 'Değişiklik Geçmişi',
  },
  sub: {
    preparation: 'Hazırlık',
    checklist: 'Hazırlık kontrol listesi',
    participants: 'Katılımcılar',
    steps: 'İcra adımları',
    criteria: 'Performans kriterleri',
    crew: 'Mürettebat performansı',
    deficiencies: 'Eksiklikler',
    stop: 'Durduruldu: gerçek acil durum',
  },
  fields: {
    vessel: 'Gemi',
    recordNo: 'Kayıt No.',
    date: 'Tarih',
    time: 'Saat (gemi saati)',
    duration: 'Süre',
    location: 'Yer',
    drill: 'Tatbikat',
    category: 'Tatbikat türü',
    responsible: 'Sorumlu Zabit',
    scenario: 'Senaryo',
    briefing: 'Emniyet brifingi verildi',
    briefingNotes: 'Brifing notları',
    equipmentReady: 'Ekipman hazır',
    equipmentNotes: 'Ekipman notları',
    weather: 'Hava',
    constraints: 'Operasyonel kısıtlar',
    ra: 'Risk değerlendirmesi doğrulaması',
    riskNotes: 'Risk notları',
    attendance: 'Katılım',
    steps: 'Adımlar',
    score: 'Puan',
    debriefing: 'Değerlendirme toplantısı',
    template: 'Şablon',
    templateVersion: 'Şablon sürümü',
    library: 'Master Library',
    vesselScenario: 'Gemi senaryosu',
    contentHash: 'İçerik özeti',
    status: 'Durum',
    stopReason: 'Gerekçe',
    stoppedAt: 'Durdurma saati',
    incident: 'Olay raporu',
    cancelReason: 'İptal gerekçesi',
  },
  columns: {
    item: 'Madde',
    done: 'Yapıldı',
    name: 'Ad',
    rank: 'Rütbe',
    role: 'Görev',
    attendance: 'Katılım',
    absence: 'Gelmeme nedeni',
    no: 'No.',
    step: 'Adım',
    result: 'Sonuç',
    time: 'Saat',
    responsible: 'Sorumlu',
    remarks: 'Açıklama',
    evidence: 'Kanıt',
    criterion: 'Kriter',
    value: 'Sonuç',
    note: 'Not',
    rating: 'Değerlendirme',
    finding: 'Bulgu No.',
    type: 'Tür',
    description: 'Açıklama',
    due: 'Hedef Tarih',
    linked: 'Bağlı kayıt',
    followUp: 'Takip',
    status: 'Durum',
    file: 'Dosya',
    size: 'Boyut',
    sha: 'SHA-256',
    open: 'Aç',
    at: 'Zaman (UTC)',
    event: 'Olay',
    by: 'Kişi',
    detail: 'Ayrıntı',
  },
  approval: {
    submitted: 'Kaptan incelemesine gönderildi',
    decision: 'Kaptan incelemesi: {{decision}}',
    reopened: 'Yeniden açıldı',
    responsibleRole: 'Sorumlu Zabit',
    master: 'Kaptan',
    deputy: 'Kaptan adına (vekil)',
    hash: 'İçerik {{hash}}',
  },
  none: {
    checklist: 'Kontrol listesi maddesi yok.',
    participants: 'Katılımcı yok.',
    steps: 'Adım yok.',
    findings: 'Bulgu açılmadı.',
    attachments: 'Kanıt eklenmedi.',
    crew: 'Mürettebat performansı girilmedi.',
    deficiencies: 'Eksiklik kaydedilmedi.',
    history: 'Geçmiş yok.',
  },
  values: {
    yes: 'Evet',
    no: 'Hayır',
    present: 'Katıldı',
    absent: 'Katılmadı',
    presentCount: '{{total}} kişiden {{present}} katıldı',
    stepsCount:
      '{{total}} adımdan {{completed}} tamamlandı, {{notCompleted}} tamamlanmadı, {{na}} uygulanmaz',
    score:
      '%{{percent}} yeterli ({{rated}} kriterden {{satisfactory}}); şirket içi yöntem, mevzuat şartı değildir',
    noScore: 'Değerlendirilen kriter yok',
    version: 's{{version}}',
    libraryNow: 'Kütüphane şimdi s{{version}}; bu kayıt s{{pinned}} ile kalır',
    libraryCurrent: 'Güncel (s{{version}})',
    scenarioYes: 'Onaylı gemi senaryosu',
    scenarioNo: 'Master Library şablonu',
    minutes: '{{count}} dk',
    evidence: '{{count}} dosya',
    attachmentCaption: '{{no}}. adım · {{name}}',
    voided: 'İptal edildi',
    notEmbedded: 'Gömülmedi; dosyayı açın',
    openLink: 'Dosyayı aç (ofis girişi)',
    openInApp: "Felixmate'da açın: kayıt, {{no}}. adım",
    redrill: 'Tekrar tatbikat istendi',
    workOrders: 'İş emirleri: {{list}}',
    requisitions: 'Talepler: {{list}}',
  },
  deficiency: {
    equipment: 'Ekipman hazır değil: {{note}}',
    step: '{{no}}. adım tamamlanmadı: {{text}}',
    criterion: '{{criterion}}: Geliştirilmeli',
  },
  versionNote:
    'Kayıt başlatıldığı şablon sürümünü korur; Master Library sonradan değişse de kayıt değişmez (ISM Drill Management §13.1).',
  attachmentsNote:
    "Fotoğraflar yukarıda gösterilir; diğer dosyalar bağlantıdan (web ofis) ya da Felixmate'da kayıttan açılır. Kanıt dosyaları gemide ve ofiste kayıtla birlikte saklanır; SHA-256 değerleriyle denetçi doğrulayabilir.",
  audit: {
    title: 'Denetim İzi',
    hint: 'Kaydın oluşturulması, düzenlemeleri, gönderimi, kaptan kararı ve yeniden açılması; değiştirilemez olay kaydından ve değişiklik kaydından.',
    empty: 'Henüz geçmiş yok.',
    onBehalf: 'kaptan adına',
    fields_one: '{{count}} alan değişti',
    fields_other: '{{count}} alan değişti',
    kinds: {
      created: 'Oluşturuldu',
      edited: 'Düzenlendi',
      submitted: 'Kaptan incelemesine gönderildi',
      approved: 'Kaptan onayladı',
      returned: 'Kaptan iade etti',
      reopened: 'Düzeltme için yeniden açıldı',
      stopped: 'Durduruldu: gerçek acil durum',
      cancelled: 'İptal edildi',
      incident_linked: 'Olay raporu bağlandı',
      finding_raised: 'Bulgu açıldı',
      finding_linked: 'Bulgu bir kayda bağlandı',
      finding_cancelled: 'Bulgu iptal edildi',
    },
    sections: {
      general: 'Genel Bilgiler',
      preparation: 'Hazırlık',
      participants: 'Katılımcılar',
      execution: 'İcra',
      evaluation: 'Değerlendirme',
      findings: 'Bulgular',
    },
  },
  archive: {
    search: 'Ara',
    searchPlaceholder: 'Kayıt No., tatbikat ya da zabit',
    from: 'Başlangıç',
    to: 'Bitiş',
    type: 'Tatbikat türü',
    allTypes: 'Bütün türler',
    selected_one: '{{count}} kayıt seçildi',
    selected_other: '{{count}} kayıt seçildi',
    limit: 'Bir dışa aktarımda en çok {{max}} kayıt; süzgeci ya da seçimi daraltın.',
    preparing: '{{total}} kayıttan {{done}} hazırlanıyor…',
    exported_one: '{{count}} kayıt dışa aktarıldı.',
    exported_other: '{{count}} kayıt dışa aktarıldı.',
    failed: 'Dışa aktarım oluşturulamadı.',
    legacy: 'Eski kayıt (ISM formu)',
    legacyHint:
      'Tatbikat modülünden önce ISM Forms’ta tutulan tatbikat raporu. Kopyalanmaz; satır asıl form kaydını açar.',
    legacyStatus: {
      pending: 'Onay bekliyor',
      returned: 'İade edildi',
      approved: 'Onaylandı',
      void: 'İptal',
      inconsistent: 'Kaydı kontrol edin',
    },
  },
  analytics: {
    description:
      'Tatbikat uyumunun filo karşılaştırması: tamamlanma oranı, gecikmeler, toplanma ve tepki süreleri, açık bulgular ve tatbikat türleri.',
    error: 'Tatbikat analitiği yüklenemedi.',
    from: 'Başlangıç',
    to: 'Bitiş',
    vessel: 'Gemi',
    allVessels: 'Bütün gemiler',
    type: 'Tatbikat türü',
    allTypes: 'Bütün türler',
    period: '{{from}} – {{to}}',
    kpi: {
      completionRate: 'Tamamlanma Oranı',
      completed: 'Tamamlanan',
      late: 'Geç ya da Yapılmayan',
      overdue: 'Şu An Gecikmiş',
      muster: 'Ort. Toplanma Süresi',
      response: 'Ort. Tepki Süresi',
      openFindings: 'Açık Bulgular',
    },
    columns: {
      vessel: 'Gemi',
      planned: 'Dönemde vadeli',
      completed: 'Tamamlanan',
      completionRate: 'Tamamlanma oranı',
      late: 'Geç / yapılmayan',
      overdue: 'Şu an gecikmiş',
      muster: 'Ort. toplanma',
      response: 'Ort. tepki',
      openFindings: 'Açık bulgu',
      month: 'Ay',
      records: 'Kayıt',
      type: 'Tatbikat türü',
      count: 'Kayıt',
      share: 'Pay',
    },
    comparison: 'Gemi karşılaştırması',
    trend: 'Toplanma ve tepki süresi eğilimi',
    trendHint: 'Kaptan onaylı kayıtların aylık ortalaması (dk:sn).',
    metric: 'Ölçü',
    metrics: { muster: 'Toplanma süresi', response: 'Tepki süresi' },
    types: 'Tatbikat türü dağılımı',
    typesHint: 'Dönemdeki kaptan onaylı kayıtlar.',
    fleet: 'Filo',
    noData: 'Dönemde kaptan onaylı tatbikat kaydı yok.',
    trendPoint: '{{vessel}}, {{month}}: {{value}}',
    sheets: { vessels: 'Gemiler', trend: 'Eğilim', types: 'Tatbikat türleri' },
    methodNote:
      'Tamamlanma oranı: dönemde vadesi gelen döngülerden vaktinde yapılanlar. Gecikmiş ve açık bulgu bugünkü durumdur. Süreler ve türler yalnız kaptan onaylı kayıtları sayar.',
    officeOnly: 'Filo analitiği ofiste kullanılır.',
  },
  competency: {
    pdf: 'PDF',
    excel: 'Excel',
    title: 'Mürettebat Yetkinlik Matrisi',
    sheet: 'Yetkinlik',
    person: 'Mürettebat',
    rank: 'Rütbe',
    last: 'Son tatbikat',
    missing: 'Eksik zorunlu tatbikat',
    trainingNeeds: 'Açık eğitim ihtiyacı',
    cell: '{{date}} · {{role}} · {{rating}}',
    never: 'Katılmadı',
    mandatory: 'Aylık zorunlu (SOLAS III/19.3.2)',
    legend:
      'Hücre: kaptan onaylı son katılım (tarih, görev, değerlendirme). “!” eksik zorunlu yangın ya da gemiyi terk tatbikatını gösterir (SOLAS III/19.3.2). Değerlendirme şirket içi yöntemdir.',
  },
};
